import moment from 'moment';
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { uniqWith } from 'lodash-es';
import { LANGUAGE_CN, LAST_LOGIN_DATE_KEY } from '@/pages/home/_config/config';
import { IComparator, IRegion, ICountry, IRegionOfRegionSelector } from '@/pages/home/types/common';
import { IHoliday, IHolidaysFetcherRes, IHolidaysMap } from '@/pages/home/types/holidays';
import { history } from 'umi';
import ReactDOM from "react-dom";
import { openWithParams } from '@/pages/dashboard/utils/utils';
import { serializeCommonValue, appendSearchParams } from '@home/utils/urlConfig';

import type { IRouteItem } from '@home/types/utils';

export const noop = () => { };

const chars = [
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
];
/**
 * 生成指定长度的随机字符串
 * @param length 字符串长度
 * @returns 指定长度的随机字符串
 */
export const generateRandomString = (length = 6): string => {
  const map = new Map<string, boolean>();
  const total = chars.length;
  let result = '';
  while (result.length < length) {
    const randomIndex = Math.floor(Math.random() * total);
    const randomChar = chars[randomIndex];
    if (!map.get(randomChar)) {
      result += randomChar;
      map.set(randomChar, true);
    }

  }
  return result;
};

/**
 * 获取指定长度的唯一key
 * @param length 字符串长度
 * @returns 获取指定长度的唯一key
 */
export const getUniqueKey = (length?: number) => {
  const randomString = generateRandomString(length);
  const timestamp = Date.now();
  return `${randomString}-${timestamp}`;
};

export const getLocalText: (locale: string, item: any, enKey: string, cnKey: string) => string = (locale, item, enKey, cnKey) => {
  if (locale === LANGUAGE_CN) {
    return item?.[cnKey] || item?.[enKey] || '';
  }
  return item?.[enKey] || item?.[cnKey] || '';
};


/**
 * 处理异常级联国家地区数据,返回正常的数据
 * @param regions 接口返回的级联国家地区数据
 * @returns 处理重复数据后的 级联国家地区数据,比如 同一个大洲 返回了两个对象, 而且 两个大洲 下的国家 不同, 需要 把这两个大洲合并为一个大洲,国家取并集
 */
export const handleRegions = (regions: IRegion[]): IRegion[] => {
  if (!Array.isArray(regions)) return [];
  const handledRegions: IRegion[] = [];
  // 国家去重的comparator
  const countryComparator: IComparator<ICountry> = (a, b) => a?.country_code === b?.country_code;

  // 遍历大洲
  for (const region of regions) {
    let regionTemp = handledRegions?.find((item) => item?.area_code === region?.area_code);
    if (!regionTemp) { // 首次遍历到当前大洲
      regionTemp = {
        ...region,
        // 对当前大洲下的国家 做去重处理
        country_set: uniqWith(region?.country_set, countryComparator),
      };
      handledRegions.push(regionTemp);
    } else { // 非首次遍历到当前大洲，表明之前的遍历 已存在当前大洲了（handledRegions数组中已存在当前大洲）
      // 合并已存在的当前大洲下的国家，和 当前遍历中的大洲 的国家
      let newCountries = [...regionTemp?.country_set, ...region?.country_set];
      // 对国家做去重处理
      newCountries = uniqWith(newCountries, countryComparator);
      regionTemp.country_set = newCountries;
    }
  }

  return handledRegions;
};

/**
 * 转换 指定接口返回的国家地区数据 为 RegionSelect组件需要的国家地区数据
 * @param regionCountries 接口返回的国家地区数据
 * @returns RegionSelect组件 需要的国家地区数据
 */
export const getRegionsForRegionSelector = (regionCountries: IRegion[]): IRegionOfRegionSelector[] => {
  if (!Array.isArray(regionCountries)) return [];
  return regionCountries?.map((regionCountry) => {
    const countries = regionCountry?.country_set?.map((country) => ({
      country_name_abbre: country?.country_code?.toUpperCase() || '',
      country_name_cn: country?.country_name_ch || '',
      country_name_en: country?.country_name_en || '',
    }));
    return {
      country: countries || [],
      region_abbre: regionCountry?.area_code || '',
      region_key: regionCountry?.area_code?.toUpperCase() || '',
      region_name_cn: regionCountry?.area_name_ch || '',
      region_name_en: regionCountry?.area_name_en || '',
    };
  });
};

/**
 * 获取指定已选region，指定国家地区列表数据 的 region对应的国家列表数据并集
 * @param regions RegionSelect组件 选择的region地区数据
 * @param regionCountries RegionSelect组件 的国家地区列表数据
 * @returns RegionSelect组件 已选region  对应的 国家的并集
 */
// const getCountriesByRegionFromRegionCountries = (regions: string[], regionCountries: IRegionOfRegionSelector[]): string[] => {
//   if (!Array.isArray(regionCountries) || !Array.isArray(regions)) return [];
//   let countries: string[] = [];
//   for (const region of regions) {
//     const regionData = regionCountries?.find(regionCountry => regionCountry?.region_abbre === region);
//     const markets = regionData?.country?.map((country) => country?.country_name_abbre || '') || [];
//     countries = [
//       ...countries,
//       ...markets,
//     ];
//   }
//   return uniq(countries);
// };

/**
 * 获取指定已选国家、指定国家地区列表数据 的 countries 对应的 地区列表数据，若已选国家中 存在某个地区下的国家，这个地区就认为是包含在返回的regions数组中
 * @param countries RegionSelect组件已选国家列表
 * @param regionCountries RegionSelect组件 的国家地区列表数据
 * @returns RegionSelect组件 已选国家 涉及的 地区
 */
// const getRegionsByCountriesFromRegionCountries = (countries: string[], regionCountries: IRegionOfRegionSelector[]): string[] => {
//   if (!Array.isArray(regionCountries)) return [];
//   const regions: string[] = [];
//   for (const regionCountry of regionCountries) {
//     // 当前地区下的国家value列表
//     const _countries = regionCountry?.country?.map((country) => country?.country_name_abbre || '') || [];
//     // 当前地区是否包含 已选国家中的某个国家，包含的话，该地区就为已选地区
//     const isContains = countries?.some((country) => _countries?.includes(country));
//     if (isContains) {
//       regions?.push(regionCountry?.region_abbre || '');
//     }
//   }
//   return regions;
// };

/**
 * 获取 指定节假日列表数据，根据节假日name、date 进行去重后的 新数据
 * @param holidays 节假日列表数据
 * @returns 处理去重后的节假日列表数据
 */
const handleHolidays = (holidays: IHoliday[]): IHoliday[] => {
  if (!Array.isArray(holidays)) return [];
  const holidayComparator: IComparator<IHoliday> = (a, b) => a?.name === b?.name && a?.observed === b?.observed;
  return (uniqWith(holidays, holidayComparator) as IHoliday[]) // 指定lodash的uniqWith方法 返回值类型为 IHoliday[]，知道如何通过泛型方式指定uniqWith方法的返回值类型 这里可以优化
    // 处理节假日的国家地区异常数据
    ?.map((item) => ({ ...item, regions: handleRegions(item?.regions) }))
    // 节假日数据排序，暂时按照节假日名称升序排序 小写字母 > 大写字母, a > b
    ?.sort((a, b) => (a?.name > b?.name) ? 1 : -1);
};

/**
 * 指定节假日列表数据，创建节假日map对象数据，用于Holidays Calendar日历组件 渲染节假日UI
 * @param data 节假日列表数据，接口返回的数据
 * @returns 节假日map，key为日期，value为节假日对象数据
 */
export const createHolidaysMap = (data: IHolidaysFetcherRes): IHolidaysMap => {
  const map = new Map<string, IHoliday[]>();
  if (!Array.isArray(data)) return map;

  // 按日期排序,这样生成的map方便 调试看数据
  const sortedData = data.sort((a, b) => (moment(a?.date).isAfter(moment(b?.date)) ? 1 : -1));

  // 构建map数据
  for (const item of sortedData) {
    const date = item?.date || '';
    const holidays = item?.holidays || [];
    // 当前日期已存的节假日数据
    let existedHolidays: IHoliday[] = [];
    if (!date) continue;
    if (!map.has(date)) {
      map.set(date, []);
    } else {
      existedHolidays = map.get(date) || [];
    }
    // 合并 当前日期已存的节假日数据、和 当前日期新的节假日数据
    map.set(date, [...existedHolidays, ...holidays]);
  }

  // 去重
  for (const [key, value] of map.entries()) {
    map.set(key, handleHolidays(value));
  }

  return map;
};

/**
 * 获取指定洲国家集合数据 对应的 国家列表数据 (级联转换为一维数组格式),返回 包含的 所有国家数据
 * @param regionCountrySet 洲国家集合数据,级联那种格式,就是国家筛选器常用的选项数据格式
 * @returns 所有国家数据,不是级联的
 */
export const getAllCountriesFromRegionCountrySet = (regionCountrySet: IRegion[]): ICountry[] => {
  const countries: ICountry[] = [];
  if (!Array.isArray(regionCountrySet)) return countries;

  for (const regionCountry of regionCountrySet) {
    const countriesTemp = regionCountry?.country_set || [];
    for (const countryTemp of countriesTemp) {

      const isExisted = countries.find((item) => item?.area_code === regionCountry?.area_code && item?.country_code === countryTemp?.country_code);
      if (!isExisted) {
        countries.push({ ...countryTemp, area_code: regionCountry?.area_code || '' });
      }
    }
  }

  return countries;
};

/**
 * 获取经分系统路径
 * @param gameCode
 * @returns 新版经分游戏路径
 */
export const getDashboardGamePath = (gameCode: string): string => {
  if (!gameCode) {
    return '';
  }
  return `/dashboard/game/${gameCode}`;
};

/**
 * 跳转经分系统
 * @param gameCode
 * @returns void
 */
export const gotoDashboardGame = (gameCode: string): void => {
  if (!gameCode) {
    return;
  }
  history.push(getDashboardGamePath(gameCode));
};

/**
 * 跳转情报系统游戏
 * @param appId
 * @returns void
 */
export const getIntelligenceGamePath = (appId: string, type: string, tab: string, subTab?: string): string => {
  const typeLinkNew = {
    mobile: '/intelligence/topCharts/mobileGames/gamesDetails',
    pc: '/intelligence/topCharts/pcGames/gamesDetails',
    console: '/intelligence/topCharts/consoleGames/gamesDetails',
    company: '/intelligence/topCharts/companies/gamesDetails',
    combine: '/intelligence/topCharts/pcConsoleGames/gamesDetails',
    following: '/intelligence/following',
  } as any;

  return `${typeLinkNew[type]}${appId ? `/${  appId}` : ''}${tab ? `/${  tab}` : ''}${tab && subTab ? `/${  subTab}` : ''}`;
};

const formatFilter = (filter?: IRecord): IRecord => {
  if (!filter) return {};
  return Object.entries(filter).reduce((acc, cur) => {
    const [key, value] = cur || [];
    return { ...acc, [key]: serializeCommonValue(value) };
  }, {});
};

export const gotoIntelligenceGame = (appId: string, type: string, tab: string, subTab?: string, unKnowParams?: any, filter?: IRecord): void => {
  const path = getIntelligenceGamePath(appId, type, tab, subTab);
  const pathNew = !filter ? path : appendSearchParams(path, formatFilter(filter));

  history.push(pathNew);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getOpinionGamePath = (gameId: string, gameType: string, idType: string, tab = 'socialMedia', path = '/opinion/Feeds/Feeds'): string => {
  if(!gameId){
    return '';
  }
  return `${path}${gameId ? `?gameid=${gameId}` : ''}`;
};
/**
 * 跳转舆情系统
 * @param gameId
 * @returns void
 */
export const gotoOpinionGame = (gameId: string, gameType: string, idType: string, tab = 'socialMedia', path = '/opinion/Feeds/Feeds'): void => {
  if (!gameId) {
    return;
  }
  // opinion/Overview?gameid=ufc454d9b1af70b40588e2a6fa4da4a8b
  // opinion/Feeds/Feeds?gameid=ufc454d9b1af70b40588e2a6fa4da4a8b

  history.push(getOpinionGamePath(gameId, gameType, idType, tab, path)); // 新风格新路径
  // history.push(`${path}${gameId ? '?gameid=' + gameId : ''}`); //新风格新路径
  // // 新旧风格路径映射
  // const pathMap = {
  //   '/opinion/Feeds/Feeds': '/opinion/Feeds/Feeds', //新风格新路径
  //   // '/opinion/Feeds/Feeds': '/opinion/feeds/',
  //   '/opinion/Overview': '/opinion/Overview'
  // } as any;
  // if (!SYSTEM_INFO.opinion[ENV]) {
  //   window.open(`${path}?gameid=${gameId}&gametype=${gameType}&idtype=${idType}${tab ? `&tab=${tab}` : ''}`);
  // } else {
  //   /** todo 新风格跳转逻辑需要根据实际情况修改 */
  //   history.push(`${pathMap[path]}${gameId ? '?gameid=' + gameId : ''}`); //新风格新路径
  //   // history.push(`${pathMap[path]}${tab}`);
  // }
};

/**
 * 修改旧系统主菜单接口返回路径，适应新风格路径
 */
// export const changeListMenu = (menuList: ListMenuObj[]): ListMenuObj[] => {
//   if (!Array.isArray(menuList)) return [];
//   const newMenuList: ListMenuObj[] = [];
//   for (const menu of menuList) {
//     if (menu.name == 'Intelligence') {
//       let menuTemp = {
//         ...menu,
//         href: `/intelligence/topCharts/mobileGames`, //临时路径
//       };
//       newMenuList.push(menuTemp);
//     } else {
//       newMenuList.push(menu);
//     }
//   }
//   return newMenuList;
// }

// React 17 批量更新
export const batchedUpdates = (callback: () => void) => {

  if (typeof ReactDOM.unstable_batchedUpdates === 'function') {
    ReactDOM.unstable_batchedUpdates(callback);
  } else {
    callback?.();
  }
};

export enum JumpType {
  Window,
  HistoryPush,
}

export const navigate = (
  url: string,
  options?: {
    // 跳转方式
    jumpType?: JumpType | string;
    // history?.push 的参数
    historyState?: any;
    // openWithParams 的参数
    windowParams?: any;
    // 是否将 historyState 传递给新窗口
    passHistoryStateToWindow?: boolean;
  },
) => {
  const { jumpType, historyState, windowParams, passHistoryStateToWindow = false } = options || {};

  let formattedJumpType = jumpType;

  if (typeof jumpType === 'string') {
    formattedJumpType = jumpType.toLowerCase();

    switch (formattedJumpType) {
      case 'window': {
        formattedJumpType = JumpType.Window;
        break;
      }
      default:
        formattedJumpType = JumpType.HistoryPush;
    }
  }

  switch (formattedJumpType) {
    // 打开新窗口
    case JumpType.Window: {
      // 根据 passHistoryStateToWindow 参数决定是否将 historyState 传递给新窗口
      const combinedParams = {
        ...windowParams,
        ...(passHistoryStateToWindow && historyState),
      };
      openWithParams(url, combinedParams);
      break;
    }
    // 默认使用 history?.push 的方式进行跳转/下钻
    default:
      history?.push(url, historyState);
  }
};


// 导出一个函数，用于判断用户是否是今天第一次登录
export const isFirstLoginToday = () => {
  // 从localStorage中获取上次登录的日期
  const lastLoginDate = localStorage.getItem(LAST_LOGIN_DATE_KEY);
  // 获取今天的日期
  const today = new Date().toDateString();

  // 如果上次登录的日期不是今天
  if (lastLoginDate !== today) {
    // 将今天的日期保存到localStorage中
    localStorage.setItem(LAST_LOGIN_DATE_KEY, today);
    // 返回true，表示是今天第一次登录
    return true;
  }
  // 否则返回false，表示不是今天第一次登录
  return false;
};

/**
 * 根据指定的分隔符拆分字符串。
 *
 * 该方法接受一个字符串参数，并根据以下字符进行拆分：
 * - 逗号 (`,`)
 * - 分号 (`;`)
 * - 竖线 (`|`)
 * - 中文逗号 (`，`)
 * - 中文分号 (`；`)
 * - 中文竖线 (`｜`)
 * - 单个或多个连续的空格
 *
 * @param input - 需要拆分的字符串
 * @returns 拆分后的字符串数组
 *
 * @example
 * const testString = "这是一个,测试;字符串|包含多种 分隔符   这里有多个空格";
 * console.log(splitStringByDelimiters(testString));
 * // 输出: ["这是一个", "测试", "字符串", "包含多种", "分隔符", "这里有多个空格"]
 */
export const splitStringByChars = (input?: string): string[] => {
  if (!input) return [];
  // 定义用于拆分字符串的字符集
  const delimiters = [',', ';', '|', '，', '；', '｜'];
  // 创建正则表达式模式，匹配任何上述定义的字符，且允许匹配多个连续出现的空格
  const regexPattern = new RegExp(`[${delimiters.map(d => `\\${d}`).join('')}]`, 'g');
  // 使用正则表达式进行拆分，并过滤掉空字符串
  return input.split(regexPattern).filter(Boolean);
};

/**
 * 将嵌套路由配置拍平为完整路径数组。
 *
 * 处理规则：
 * - 支持从 `routes` 或 `children` 字段递归读取子路由；
 * - 相对路径会基于 `basePath` 进行拼接；
 * - `./xxx` 会被标准化为 `xxx`，`.` 会被视为当前层级路径；
 * - 绝对路径（以 `/` 开头）会直接保留；
 * - 通配符路由 `*` 本身不会写入结果，但仍会继续递归其子路由；
 * - 当入参为空数组或非数组时，返回空数组。
 *
 * @param routes 路由配置列表
 * @param basePath 当前递归层级使用的基础路径，默认空字符串
 * @returns 拍平后的完整路径列表
 *
 * @example
 * flattenUrlCollectionRoutes([
 *   { path: '/home' },
 *   {
 *     path: '/intelligence',
 *     routes: [{ path: 'detail' }, { path: './compare' }, { path: '.' }],
 *   },
 * ]);
 * // ['/home', '/intelligence', '/intelligence/detail', '/intelligence/compare', '/intelligence']
 */
export const flattenUrlCollectionRoutes = (routes: IRouteItem[], basePath = ''): string[] => {
  if (!Array.isArray(routes) || !routes.length) {
    return [];
  }

  const result: string[] = [];

  for (const route of routes) {
    let routePath = route?.path || '';

    if (routePath.startsWith('./')) {
      routePath = routePath.slice(2);
    }

    if (routePath === '.') {
      routePath = '';
    }

    let fullPath = '';

    if (routePath === '*') {
      fullPath = '';
    } else if (!routePath) {
      fullPath = basePath;
    } else if (routePath.startsWith('/')) {
      fullPath = routePath;
    } else if (basePath) {
      fullPath = `${basePath}/${routePath}`;
    } else {
      fullPath = routePath;
    }

    if (fullPath) {
      result.push(fullPath);
    }

    const children = route?.routes || route?.children || [];

    if (Array.isArray(children) && children.length > 0) {
      result.push(...flattenUrlCollectionRoutes(children, fullPath || basePath));
    }
  }

  return result;
};
